import { FormRow, IntegerField, TextField } from '../DebitStudentCoins';
import { FC, PropsWithChildren, ReactElement } from 'react';
import { FormikErrors, useFormik } from 'formik';
import '../DebitStudentCoins/module.scss';
import { v4 as uuidV4 } from 'uuid';
import { ColorRing } from 'src/components/shared/loader';
import { PrimaryButton } from 'src/components/design-system/form/buttons/PrimaryButton';
import { SecondaryButton } from 'src/components/design-system/form/buttons/SecondaryButton';

export type CreditStudentCoinsFormValues = {
    amount: number;
    description: string;
};

export type CreditStudentCoinsFormProps = {
    studentId: number;
    onSubmit?: (values: CreditStudentCoinsFormValues) => Promise<void>;
    onCancel?: () => void;
};

export const LoadableLabel = (
    props: PropsWithChildren<{ isLoading?: boolean; color?: string }>
) => {
    const { children, isLoading = false, color = '#FFFFFF' } = props;

    return (
        <div
            className={'loadable-label'}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                zIndex: 2,
                marginLeft: isLoading ? '-5px' : undefined,
            }}
        >
            <ColorRing
                height={'25px'}
                width={'25px'}
                color={color}
                visible={isLoading}
                strokeWidth={7}
            />
            {children}
        </div>
    );
};

export const CreditStudentCoinsForm: FC<CreditStudentCoinsFormProps> = (
    props: CreditStudentCoinsFormProps
): ReactElement => {
    const { onSubmit } = props;

    const formik = useFormik<Partial<CreditStudentCoinsFormValues>>({
        initialValues: {
            amount: undefined,
            description: '',
        },
        validate: (
            values: Partial<CreditStudentCoinsFormValues>
        ): FormikErrors<Partial<CreditStudentCoinsFormValues>> => {
            const errors: FormikErrors<Partial<CreditStudentCoinsFormValues>> =
                {};

            const { amount, description } = values;

            if (amount === undefined) {
                errors.amount = 'Поле должно быть заполнено';
            } else {
                if (amount === 0) {
                    errors.amount = 'Значение должно быть положительным числом';
                }

                if (amount > 1000) {
                    errors.amount =
                        'Ну ты это, ты не мороси. Так делать нельзя';
                }
            }

            if (!description || !description.trim().length) {
                errors.description = 'Поле должно быть заполнено';
            }

            return errors;
        },
        onSubmit: async values => {
            if (onSubmit)
                await onSubmit(values as CreditStudentCoinsFormValues);
        },
    });

    return (
        <form autoComplete={'off'} onSubmit={formik.handleSubmit}>
            <FormRow>
                <IntegerField
                    autoFocus={true}
                    label={'Сумма к начислению'}
                    placeholder={'Введите сумму'}
                    formik={formik}
                    name={'amount'}
                    description={'Положительное число'}
                />
            </FormRow>

            <FormRow>
                <TextField
                    label={'Описание'}
                    placeholder={'Введите описание'}
                    formik={formik}
                    name={'description'}
                    description={'Причина начисления средств'}
                />
            </FormRow>

            <div className={'form-buttons'}>
                <SecondaryButton label={'Отменить'} onClick={props.onCancel} />

                <PrimaryButton
                    label={'Начислить'}
                    formik={formik}
                    submit={true}
                />
            </div>

            <input
                name={uuidV4()}
                type={'text'}
                style={{ display: 'none' }}
                autoComplete={'on'}
            />
        </form>
    );
};
